// Marketing helpers — audience filtering, variable interpolation, wa.me link builder.

/**
 * Compute eligible clients for a given campaign trigger.
 * Returns array of {client, context} where context has extra data for interpolation.
 */
export const computeEligibleClients = (campaign, allClients, allAppointments, brand) => {
    if (!campaign || !campaign.trigger) return [];
    const { type, config = {} } = campaign.trigger;
    const now = Date.now();
    const todayDate = new Date();

    if (type === "inactive") {
        const cutoff = now - (config.daysAway || 45) * 86400000;
        return allClients
            .filter((c) => {
                const txns = c.transactions || [];
                if (txns.length === 0) return false;
                const last = Math.max(...txns.map((t) => t.date || 0));
                if (last === 0 || last >= cutoff) return false;
                // Optional category filter (best-effort: matches last service name)
                if (config.serviceCategory) {
                    const lastTxn = txns.find((t) => t.date === last);
                    if (lastTxn && !lastTxn.service?.toLowerCase().includes(config.serviceCategory.toLowerCase())) {
                        return false;
                    }
                }
                return true;
            })
            .map((c) => {
                const last = Math.max(...(c.transactions || []).map((t) => t.date || 0));
                return {
                    client: c,
                    context: {
                        dias_inativo: Math.floor((now - last) / 86400000),
                        ultimo_servico: (c.transactions || []).find((t) => t.date === last)?.service || "atendimento",
                    },
                };
            });
    }

    if (type === "birthday-month") {
        const targetMonth = todayDate.getMonth() + 1;
        return allClients
            .filter((c) => {
                if (!c.birthDate) return false;
                const [, m] = c.birthDate.split("-").map(Number);
                return m === targetMonth;
            })
            .map((c) => {
                const [, m, d] = c.birthDate.split("-").map(Number);
                return {
                    client: c,
                    context: {
                        dia_aniversario: `${String(d).padStart(2, "0")}/${String(m).padStart(2, "0")}`,
                        servico_nome: "barba",
                    },
                };
            });
    }

    if (type === "upsell") {
        const base = (config.baseService || "").toLowerCase();
        return allClients
            .filter((c) => {
                const txns = c.transactions || [];
                if (txns.length === 0) return false;
                return txns.some((t) => (t.service || "").toLowerCase().includes(base));
            })
            .map((c) => ({
                client: c,
                context: {
                    ultimo_servico: config.baseService || "corte",
                    servico_sugerido: config.suggestService || "hidratação",
                },
            }));
    }

    if (type === "post-service") {
        // Clients who finished an appointment in the last 24h
        const cutoff = now - 24 * 3600000;
        const recentFinished = allAppointments.filter(
            (a) => a.status === "concluido" && (a.finished_at || a.date) >= cutoff
        );
        const seen = new Set();
        const result = [];
        recentFinished.forEach((a) => {
            const c = allClients.find((cl) => cl.id === a.clientId);
            if (!c || seen.has(c.id)) return;
            seen.add(c.id);
            result.push({
                client: c,
                context: {
                    servico_nome: a.serviceName,
                    profissional_nome: a.professionalName,
                },
            });
        });
        return result;
    }

    if (type === "referral") {
        return allClients
            .filter((c) => c.paymentStatus === "VIP" || (c.totalSpent || 0) > 400)
            .map((c) => ({
                client: c,
                context: {
                    codigo_indicacao: `KING-${c.id.toUpperCase()}`,
                    link_indicacao: `${brand?.shopId || "salon"}.kinng.app/indicacao/${c.id}`,
                },
            }));
    }

    return [];
};

/**
 * Interpolate template variables into a message body.
 */
export const interpolateTemplate = (template, client, context = {}, brand = {}) => {
    if (!template || !client) return "";
    const vars = {
        cliente_nome: client.name?.split(" ")[0] || "amigo",
        cliente_nome_completo: client.name || "",
        salao_nome: brand.salonName || "Kinng.app",
        link_agendamento: `${brand.shopId || "salon"}.kinng.app/agendar`,
        ...context,
    };
    return template.body.replace(/\{(\w+)\}/g, (_, key) => vars[key] ?? `{${key}}`);
};

/**
 * Build a wa.me link with prefilled message.
 */
export const buildWhatsAppLink = (phone, message) => {
    const digits = (phone || "").replace(/\D/g, "");
    // Ensure Brazil country code
    const phoneFinal = digits.startsWith("55") ? digits : `55${digits}`;
    return `https://wa.me/${phoneFinal}?text=${encodeURIComponent(message)}`;
};

/** Trigger type metadata for UI rendering */
export const TRIGGER_META = {
    inactive: {
        label: "Cliente inativo",
        icon: "fa-user-clock",
        color: "amber",
        desc: "Dispara quando o cliente passa X dias sem agendar",
    },
    "birthday-month": {
        label: "Aniversariante do mês",
        icon: "fa-cake-candles",
        color: "rose",
        desc: "Dispara no início do mês de aniversário",
    },
    "post-service": {
        label: "Pós-atendimento",
        icon: "fa-star",
        color: "emerald",
        desc: "Dispara após cada serviço concluído",
    },
    upsell: {
        label: "Up-sell complementar",
        icon: "fa-arrow-up-right-dots",
        color: "sky",
        desc: "Sugere serviços complementares com base no histórico",
    },
    referral: {
        label: "Indicação (MGM)",
        icon: "fa-people-arrows",
        color: "violet",
        desc: "Programa Member Get Member com código único",
    },
};

export const AUDIENCE_PRESETS = [
    { id: "inactive45", label: "Inativos há 45 dias", trigger: { type: "inactive", config: { daysAway: 45 } }, icon: "fa-user-clock" },
    { id: "inactive30", label: "Inativos há 30 dias", trigger: { type: "inactive", config: { daysAway: 30 } }, icon: "fa-user-clock" },
    { id: "birthday-month", label: "Aniversariantes do mês", trigger: { type: "birthday-month", config: {} }, icon: "fa-cake-candles" },
    { id: "post-service", label: "Atendidos hoje", trigger: { type: "post-service", config: {} }, icon: "fa-star" },
    { id: "vip-referral", label: "VIPs (indicação)", trigger: { type: "referral", config: {} }, icon: "fa-crown" },
];
